// Free of value imports on purpose: the install-free test step cannot resolve `../consent.js` at
// runtime (see test/core.test.ts), and this module is tested there.

/**
 * Asks the integrator's reader if there is one, the fallback otherwise.
 *
 * `PulseProvider` memoizes the tracker on the config's serialized value, and `JSON.stringify`
 * drops functions — so a `consent` backed by React or CMP-hook state never changes the key, the
 * tracker is never rebuilt, and a reader captured at construction stays frozen at its first
 * render. The provider therefore hands the tracker and the withdrawal watcher a wrapper that calls
 * this with the latest `config.consent` on every read.
 */
export function resolveConsent(configured: (() => boolean) | undefined, fallback: () => boolean): boolean {
    return (configured ?? fallback)();
}

/**
 * The withdrawal transition logic, free of React so it can be tested without a renderer.
 *
 * Reads consent once immediately, then again on every `eventName` dispatched on `target`, and calls
 * `onWithdrawn` only on a true → false change. Returns the cleanup.
 *
 * `previous` is what consent was last *seen* to be, not whether a withdrawal was sent: a visitor
 * may withdraw, re-grant and withdraw again, and each is a real transition. It starts `null` —
 * "not looked yet" — so the first read cannot look like a flip; otherwise a visitor who arrives
 * with consent already denied would send a withdrawal on every page load.
 */
export function watchConsentWithdrawal(options: {
    readConsent: () => boolean;
    eventName: string;
    target: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
    onWithdrawn: () => void;
}): () => void {
    const { readConsent, eventName, target, onWithdrawn } = options;
    let previous: boolean | null = null;

    function sync() {
        const granted = readConsent();
        const wasGranted = previous;
        previous = granted;

        // The banner dispatches on every save, including saves that change nothing and saves of a
        // different category, so comparing against the previous value keeps one visitor action to
        // one event.
        if (wasGranted === true && !granted) onWithdrawn();
    }

    sync();
    target.addEventListener(eventName, sync);

    return () => target.removeEventListener(eventName, sync);
}
